import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import { useTheme, type Theme } from '../../theme';
import { borderWidth, s, v } from '../../utils/libs';

type RecentTransactionsListSkeletonProps = {
  count?: number;
};

const ROW_MIN_HEIGHT = v(78);

const RecentTransactionsListSkeleton: React.FC<
  RecentTransactionsListSkeletonProps
> = ({ count = 5 }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const rows = Array.from({ length: Math.max(1, count) });

  return (
    <SkeletonPlaceholder
      backgroundColor={theme.colors.border}
      highlightColor={theme.colors.surfaceSecondary}>
      <View style={styles.list}>
        {rows.map((_, idx) => (
          <View
            key={String(idx)}
            style={[
              styles.rowShell,
              idx < rows.length - 1 ? styles.rowShellSpacing : null,
            ]}>
            <View style={[styles.railShim, { height: ROW_MIN_HEIGHT }]} />
            <View style={styles.rowBody}>
              <View style={styles.rowTop}>
                <View style={styles.customerBar} />
                <View style={styles.receiptBar} />
              </View>
              <View style={styles.metaBar} />
              <View style={styles.rowBottom}>
                <View style={styles.amountBar} />
                <View style={styles.chipsRow}>
                  <View style={styles.chipPill} />
                  <View style={styles.chipPillWide} />
                </View>
              </View>
            </View>
          </View>
        ))}
      </View>
    </SkeletonPlaceholder>
  );
};

export default RecentTransactionsListSkeleton;

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    list: {
      width: '100%',
    },
    rowShell: {
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: ROW_MIN_HEIGHT,
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
      overflow: 'hidden',
    },
    rowShellSpacing: {
      marginBottom: v(6),
    },
    /** Thin strip matching `statusRail` width in RecentTransactionsSection */
    railShim: {
      width: s(5),
    },
    rowBody: {
      flex: 1,
      paddingHorizontal: s(10),
      paddingVertical: v(6),
      gap: v(2),
      justifyContent: 'center',
    },
    rowTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(8),
    },
    customerBar: {
      width: '55%',
      height: v(14),
      borderRadius: theme.borderRadius.sm,
    },
    receiptBar: {
      width: '38%',
      height: v(12),
      borderRadius: theme.borderRadius.sm,
    },
    metaBar: {
      width: '48%',
      height: v(12),
      borderRadius: theme.borderRadius.sm,
    },
    rowBottom: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(6),
    },
    amountBar: {
      width: '34%',
      height: v(14),
      borderRadius: theme.borderRadius.sm,
    },
    chipsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
    },
    chipPill: {
      width: s(52),
      height: v(18),
      borderRadius: theme.borderRadius.round,
    },
    chipPillWide: {
      width: s(64),
      height: v(18),
      borderRadius: theme.borderRadius.round,
    },
  });
